/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { ChannelResource, DeploymentProcessResource, DeploymentTargetResource, EnvironmentV2Resource, LifecycleResource, ProjectResource, ResourceCollection, TenantResource, VariableResource, VariableSetResource, } from "@octopusdeploy/octopus-server-client";
import { mapFromEnvironmentResourceToEnvironmentV2Resource, Permission, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { flatMap } from "lodash";
import type { FC } from "react";
import * as React from "react";
import { useProjectContext } from "~/areas/projects/context";
import type { WithProjectContextInjectedProps } from "~/areas/projects/context/withProjectContext";
import VariableDisplayer, { mergeAndSortVariables, type FilteredVariable, type ValueWithSource, type VariableWithSource } from "~/areas/variables/VariableDisplayer";
import { convertVariableResourcesToVariablesWithSource } from "~/areas/variables/convertVariableResourcesToVariablesWithSource";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import FilterSearchBox from "~/components/FilterSearchBox";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { SimplePagingList } from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SidebarLayout, { SidebarSide } from "~/components/SidebarLayout/SidebarLayout";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import { useEphemeralEnvironmentsEnabled } from "../../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { VcsError } from "../../VersionControl/VcsError";
import { VariableExpressionTesterTab } from "./VariableExpressionTesterTab";
import VariablePreviewSummary from "./VariablePreviewSummary";
import { VariableScopeSelector } from "./VariableScopeSelector";
interface VariableListProps {
    variables: VariableResource[];
}
class VariableList extends SimplePagingList<VariableResource> {
}
const Variables: FC<VariableListProps> = (props) => {
    const onRow = (variable: VariableResource) => [<b>{variable.Name}</b>, variable.Value];
    return <VariableList items={props.variables} onRow={onRow} empty={<span>No variables have been added</span>}/>;
};
Variables.displayName = "Variables";
interface SharedVariablePreviewPageState extends DataBaseComponentState {
    variableNameFilter: string;
    environmentId?: string;
    targetId?: string;
    channelId?: string;
    tenantId?: string;
    actionId?: string;
    machineId?: string;
    project?: ProjectResource;
    environments?: EnvironmentV2Resource[];
    targets?: DeploymentTargetResource[];
    channels?: Item[];
    tenants?: Item[];
    actions?: Item[];
    machines?: Item[];
    variables?: VariableSetResource;
    showOctopus: boolean;
    filterEmptyValues: boolean;
    currentTab: "preview" | "test";
}
type SharedVariablePreviewPageInternalProps = WithProjectContextInjectedProps & {
    ephemeralEnvironmentsAreEnabled: boolean;
};
const pageTitle = "Variable Preview";
class SharedVariablePreviewPageInternal extends DataBaseComponent<SharedVariablePreviewPageInternalProps, SharedVariablePreviewPageState> {
    constructor(props: SharedVariablePreviewPageInternalProps) {
        super(props);
        this.state = {
            showOctopus: false,
            filterEmptyValues: false,
            variableNameFilter: null!,
            currentTab: "preview",
        };
    }
    async componentDidMount() {
        await this.loadData();
    }
    async componentDidUpdate(prevProps: SharedVariablePreviewPageInternalProps) {
        if (prevProps.projectContext.state.gitRef !== this.props.projectContext.state.gitRef) {
            await this.loadVariables();
        }
    }
    render() {
        if (!this.state.variables || !this.state.project) {
            return (<PageContent header={{ title: pageTitle }} busy={Promise.resolve()} errors={this.errors}>
                    {undefined}
                </PageContent>);
        }
        const summary = (<VariablePreviewSummary tenantedDeploymentMode={this.state.project && this.state.project.TenantedDeploymentMode} environments={this.state.environments!} environmentId={this.state.environmentId!} tenants={this.state.tenants!} tenantId={this.state.tenantId!} actions={this.state.actions!} actionId={this.state.actionId!} channels={this.state.channels!} channelId={this.state.channelId!} machines={this.state.machines!} machineId={this.state.machineId!}/>);
        const sidebar = this.state.variables && (<VariableScopeSelector project={this.state.project!} environments={this.state.environments!} environmentId={this.state.environmentId} tenants={this.state.tenants!} tenantId={this.state.tenantId} actions={this.state.actions!} actionId={this.state.actionId} channels={this.state.channels!} channelId={this.state.channelId} machines={this.state.machines!} machineId={this.state.machineId} showOctopus={this.state.showOctopus} filterEmptyValues={this.state.filterEmptyValues} onEnvironmentChanged={this.handleEnvironmentChanged} onTenantChanged={this.handleTenantChanged} onActionChanged={this.handleActionChanged} onChannelChanged={this.handleChannelChanged} onMachineChanged={this.handleMachineChanged} onShowOctopusChanged={this.handleShowOctopusChanged} onFilterEmptyValuesChanged={this.handleFilterEmptyValuesChanged}/>);
        const variableSections = mergeAndSortVariables(this.getVariables(), this.state.variables.ScopeValues);
        const filteredVariableSections = variableSections.map((v) => {
            return this.mapToFilteredVariable(v);
        });
        const previewContent = (<SidebarLayout sideBar={sidebar} side={SidebarSide.Left} extendContentToEdges={true} extendSidebarToEdges={true} hideTopDivider={true} hideSidebarDivider={false} overflowXHidden={true}>
                <VariableDisplayer availableScopes={this.state.variables && this.state.variables.ScopeValues} variableSections={[filteredVariableSections]} doBusyTask={this.doBusyTask} hideSource={true} hideScope={false} isDisplayingFullWidth={false} ephemeralEnvironmentsAreEnabled={this.props.ephemeralEnvironmentsAreEnabled}/>
            </SidebarLayout>);
        const variableNames = this.state.variables?.Variables.map((v) => v.Name).sort((a, b) => {
            if (a.startsWith("Octopus.") && !b.startsWith("Octopus.")) {
                return 1;
            }
            if (!a.startsWith("Octopus.") && b.startsWith("Octopus.")) {
                return -1;
            }
            return a.localeCompare(b);
        }) || [];
        const testContent = (<VariableExpressionTesterTab project={this.state.project!} environments={this.state.environments!} environmentId={this.state.environmentId} tenants={this.state.tenants!} tenantId={this.state.tenantId} actions={this.state.actions!} actionId={this.state.actionId} channels={this.state.channels!} channelId={this.state.channelId} machines={this.state.machines!} machineId={this.state.machineId} gitRef={this.props.projectContext.state.gitRef?.CanonicalName} showOctopus={this.state.showOctopus} filterEmptyValues={this.state.filterEmptyValues} variableNames={variableNames} onEnvironmentChanged={this.handleEnvironmentChanged} onTenantChanged={this.handleTenantChanged} onActionChanged={this.handleActionChanged} onChannelChanged={this.handleChannelChanged} onMachineChanged={this.handleMachineChanged} onShowOctopusChanged={this.handleShowOctopusChanged} onFilterEmptyValuesChanged={this.handleFilterEmptyValuesChanged}/>);
        //TODO: FEF - Deal with sidebar layout usage, which looks like its usage is more like advanced filters, but not quite.
        return (<PageContent header={{ title: pageTitle }} busy={this.state.busy} errors={this.errors} currentTab={this.state.currentTab} onTabChanged={this.handleTabChanged} tabs={[
                {
                    label: "Preview",
                    value: "preview",
                    content: previewContent,
                    filters: { inputs: [<FilterSearchBox placeholder="By variable name" value={this.state.variableNameFilter} onChange={this.handleFilterChanged}/>], filtersSummary: summary },
                },
                {
                    label: "Test",
                    value: "test",
                    content: testContent,
                },
            ]}/>);
    }
    private mapToFilteredVariable(variable: VariableWithSource): FilteredVariable {
        return {
            name: variable.name,
            variableMessages: null!,
            values: variable.values.map((x) => {
                const value = x as ValueWithSource;
                return {
                    ...value,
                    messages: null!,
                };
            }),
        };
    }
    private handleFilterChanged = (variableNameFilter: string) => {
        this.setState({ variableNameFilter });
    };
    private async loadEnvironmentsFromLifecycle(lifecycle: LifecycleResource) {
        if (!isAllowed({ permission: Permission.EnvironmentView, environment: "*" })) {
            return [];
        }
        const environmentIds = flatMap(lifecycle.Phases, (phase) => {
            return phase.AutomaticDeploymentTargets.concat(phase.OptionalDeploymentTargets);
        });
        const args = environmentIds.length === 0 ? undefined : { ids: environmentIds };
        const environments = await repository.Environments.all(args);
        return environments.map(mapFromEnvironmentResourceToEnvironmentV2Resource);
    }
    private async loadEnvironmentsFromParentEnvironment(spaceId: string, parentEnvironmentId: string) {
        if (!isAllowed({ permission: Permission.EnvironmentView, environment: "*" })) {
            return [];
        }
        const environments = await repository.Environments.getV2({ spaceId: spaceId, skip: 0, take: Repository.takeAll, type: ["Parent"] });
        const parentEnvironment = environments.Items.find((e) => e.Id === parentEnvironmentId);
        return parentEnvironment ? [parentEnvironment] : [];
    }
    private async loadData() {
        await this.doBusyTask(async () => {
            const { model: project, projectContextRepository } = this.props.projectContext.state;
            const gitRef = this.props.projectContext.state.gitRef?.CanonicalName;
            const hasProcessViewPermissions = isAllowed({
                permission: Permission.ProcessView,
                project: project.Id,
                tenant: "*",
            });
            const [deploymentProcess, channels, lifecycle, tenants] = await Promise.all([
                hasProcessViewPermissions ? projectContextRepository.DeploymentProcesses.get() : Promise.resolve<DeploymentProcessResource>(null!),
                hasProcessViewPermissions ? repository.Projects.getChannels(project, 0, 1000) : Promise.resolve<ResourceCollection<ChannelResource>>(null!),
                repository.Lifecycles.get(project.LifecycleId),
                isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all({ projectId: project.Id }) : Promise.resolve<TenantResource[]>([]),
            ]);
            const environments = await this.loadEnvironmentsFromLifecycle(lifecycle);
            const tenantId = tenants && tenants.length > 0 ? tenants[0].Id : null!;
            const environmentId = environments && environments.length > 0 ? environments[0].Id : null!;
            const actionId = deploymentProcess && deploymentProcess.Steps.length > 0 ? deploymentProcess.Steps[0].Actions[0].Id : null!;
            const channelId = channels && channels.Items.length > 0 ? channels.Items[0].Id : null!;
            const variablesPromise = repository.Variables.preview(project.Id, null!, actionId, environmentId, null!, channelId, tenantId, gitRef);
            const machinesPromise = this.loadMachinesForEnvironment(environmentId!);
            const [variables, machines] = await Promise.all([variablesPromise, machinesPromise]);
            const actions = variables.ScopeValues.Actions.map((a) => ({ text: a.Name, value: a.Id }));
            this.setState({
                project,
                environments: environments,
                environmentId,
                channels: channels.Items.map((c) => ({ text: c.Name, value: c.Id })),
                channelId,
                tenants: tenants.map((t) => ({ text: t.Name, value: t.Id })),
                tenantId,
                machines,
                actions,
                actionId,
                variables,
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    private loadMachinesForEnvironment = async (environmentId: string) => {
        if (!environmentId || !isAllowed({ permission: Permission.MachineView, wildcard: true })) {
            return [];
        }
        const machines = await repository.Machines.list({ environmentIds: environmentId, take: 999 });
        return machines.Items.map((m) => ({ text: m.Name, value: m.Id }));
    };
    private handleEnvironmentChanged = async (environmentId: string | undefined) => {
        await this.doBusyTask(async () => {
            const machines = await this.loadMachinesForEnvironment(environmentId!);
            const machine = machines.find((m) => m.value === this.state.machineId)!;
            const machineId = machine ? machine.value : null!;
            this.setState({
                environmentId,
                machines,
                machineId,
            }, () => this.loadVariables());
        });
    };
    private handleMachineChanged = (machineId: string | undefined) => {
        this.setState({ machineId }, () => this.loadVariables());
    };
    private handleChannelChanged = async (channelId: string | undefined) => {
        await this.doBusyTask(async () => {
            const channel = await repository.Channels.getFromProject(this.state.project!, channelId!);
            let environments: EnvironmentV2Resource[] = [];
            if (channel.Type === "Lifecycle") {
                const lifecycleId = channel.LifecycleId ?? this.state.project!.LifecycleId;
                const lifecycle = await repository.Lifecycles.get(lifecycleId);
                environments = await this.loadEnvironmentsFromLifecycle(lifecycle);
            }
            else if (channel.Type === "EphemeralEnvironment") {
                environments = await this.loadEnvironmentsFromParentEnvironment(channel.SpaceId, channel.ParentEnvironmentId!);
            }
            let environmentId = environments.some((x) => x.Id === this.state.environmentId) ? this.state.environmentId : null!;
            if (!environmentId && environments.length === 1) {
                environmentId = environments[0].Id;
                this.handleEnvironmentChanged(environmentId);
            }
            this.setState({
                environments: environments,
                channelId,
                environmentId,
            }, () => this.loadVariables());
        });
    };
    private handleTenantChanged = (tenantId: string | undefined) => {
        this.setState({ tenantId }, () => this.loadVariables());
    };
    private handleActionChanged = (actionId: string | undefined) => {
        this.setState({ actionId }, () => this.loadVariables());
    };
    private handleFilterEmptyValuesChanged = (showEmptyValues: boolean) => {
        this.setState({ filterEmptyValues: showEmptyValues });
    };
    private handleShowOctopusChanged = (showOctopus: boolean) => {
        this.setState({ showOctopus });
    };
    private handleTabChanged = (value: string) => {
        if (value === "preview" || value === "test") {
            this.setState({ currentTab: value });
        }
    };
    private getVariables = () => {
        function isEmpty(value?: string | null) {
            return value ? false : true;
        }
        if (!this.state.variables) {
            return [];
        }
        // We don't show the source on the variable preview page. We use this source
        // in the call to convertVariableResourcesToVariablesWithSource so that it
        // has _a_ source, but it's setting everything to project which is not correct.
        const source = {
            projectName: this.state.project!.Name!,
            projectId: this.state.project!.Id!,
            spaceId: this.state.project!.SpaceId,
        };
        const filtered = this.state.variables.Variables.filter((v) => !this.state.filterEmptyValues || isEmpty(v.Value))
            .filter((v) => this.state.showOctopus || !v.Name.startsWith("Octopus."))
            .filter((v) => !this.state.variableNameFilter || this.state.variableNameFilter.length === 0 || v.Name.toLowerCase().includes(this.state.variableNameFilter.toLowerCase()));
        return convertVariableResourcesToVariablesWithSource(filtered, source);
    };
    private loadVariables = async () => {
        const gitRef = this.props.projectContext.state.gitRef?.CanonicalName;
        await this.doBusyTask(async () => {
            const variables = await repository.Variables.preview(this.state.project!.Id, null!, this.state.actionId!, this.state.environmentId!, this.state.machineId!, this.state.channelId!, this.state.tenantId!, gitRef);
            const actions = variables.ScopeValues.Actions.map((a) => ({ text: a.Name, value: a.Id }));
            this.setState({
                variables,
                actions,
            });
        }, { timeOperationOptions: timeOperationOptions.forRefresh() });
    };
    static displayName = "SharedVariablePreviewPageInternal";
}
function SharedVariablePreviewPage() {
    const projectContext = useProjectContext();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const spaceNavigation = useSpaceAwareNavigation();
    const { model: project, gitRef, gitRefValidationError } = projectContext.state;
    const navigateToGitRef = (gitRef: string) => {
        spaceNavigation.navigate(links.branchVariablePreviewPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }));
    };
    if (gitRefValidationError && gitRefValidationError.Type !== "Success") {
        return (<PageContent header={{ title: pageTitle }}>
                <VcsError project={project} error={gitRefValidationError} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>
            </PageContent>);
    }
    return <SharedVariablePreviewPageInternal projectContext={projectContext} ephemeralEnvironmentsAreEnabled={ephemeralEnvironmentsAreEnabled}/>;
}
export default SharedVariablePreviewPage;
